import { useState, useEffect, useReducer, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSelector } from 'reselect'
import * as ACT from './actions'
import { EmailContactModal } from './EmailContactModal'
import { toUsersSelect, toContactsSelect } from './serializers'
import { makeGetRequest, makeLeadsGetRequest } from 'utils/api'
import { toEmailTemplatesSelect } from 'components/Contact/serializers'
import { getUser } from 'utils/authService'
import isEmpty from 'lodash/isEmpty'
import update from 'immutability-helper'
import { EMAIL_TEMPLATE_TYPE } from 'utils/constants'

const selectUsers = createSelector([state => state.users], users => {
  return toUsersSelect(users)
})

const selectContacts = createSelector([state => state.contacts], contacts => {
  return toContactsSelect(contacts)
})

const initialState = {
  fromUser: {},
  ccContacts: '',
  bccContacts: '',
  files: []
}

const reducer = (state, action) => {
  const { value, property, file } = action.payload
  let index
  switch (action.type) {
    case ACT.CHANGE_EMAIL_CONTACTS:
      return { ...state, [property]: value }
    case ACT.RESET_EMAIL_CONTACTS:
      return initialState
    case ACT.ADD_FILE:
      index = state.files.findIndex(f => f.name === file.name)
      if (~index) {
        return state
      } else {
        return update(state, {
          files: { $unshift: [file] }
        })
      }
    case ACT.REMOVE_FILE:
      index = state.files.findIndex(f => f.name === file.name)
      return update(state, {
        files: {
          $splice: [[index, 1]]
        }
      })
    default:
      return state
  }
}

const getProposalEmailBody = async (id, cb) => {
  const url = `/v1/proposals/email/template/${id}`
  const emailTemplate = await makeGetRequest(url)

  cb && cb(emailTemplate)
}

const getPreselectedEmailTemplate = async (templateType, cb) => {
  let url
  if (templateType === EMAIL_TEMPLATE_TYPE.COMPANY_OPEN_OPPORTUNITIES) {
    url = '/v1/email/openopportunitiesid'
  } else if (templateType === EMAIL_TEMPLATE_TYPE.CONTACT_OPEN_OPPORTUNITIES) {
    url = '/v1/email/openopportunitiesWithContactId'
  }

  if (url) {
    const templateId = await makeGetRequest(url)
    cb && cb(templateId)
  }
}

const getEmailTemplates = async (app, cb, isBulk) => {
  let templates

  if (app === 'leads') {
    templates = isBulk
      ? await makeLeadsGetRequest('/v1/email/templates/bulk')
      : await makeLeadsGetRequest('/v1/email')
  } else {
    templates = await makeGetRequest('/v1/email/templates/contacts')
  }

  cb && cb(toEmailTemplatesSelect(templates))
}

const getEmailTemplate = async (contactId, templateId, fromUser, cb, app, leadId) => {
  const url = `/v1/email/templates/contacts/${contactId}/${templateId}`
  const headerParams = {
    'x-uid': fromUser
  }
  const leadUrl = leadId
    ? `/v1/email/templates/${leadId}/${templateId}`
    : `/v1/email/templates/bulk/resolve/${templateId}`
  let template
  if (!isEmpty(fromUser)) {
    if (app === 'leads') {
      template = await makeLeadsGetRequest(leadUrl)
    } else {
      template = await makeGetRequest(url)
    }
  } else {
    if (app === 'leads') {
      template = await makeLeadsGetRequest(leadUrl, {
        headers: headerParams
      })
    } else {
      template = await makeGetRequest(url, { headers: headerParams })
    }
  }

  cb && cb(template)
}

const EmailContactModalContainer = ({
  proposalId,
  toggleEmailContactModal,
  showEmailContactModal,
  contact,
  emailTemplateType,
  removeContact,
  emailSubject = '',
  fromContactsList,
  withSaveEmailAsNote,
  entityId,
  defaultEmailSplitMode,
  app,
  leadId
}) => {
  const [subject, setSubject] = useState(emailSubject)
  const [emailBody, setEmailBody] = useState('')
  const [toContact, setToContact] = useState(null)
  const [template, setTemplate] = useState(null)
  const [templates, setTemplates] = useState([])
  const [emailInfo, dispatchState] = useReducer(reducer, initialState)
  const [allCCContacts, setAllCCContacts] = useState([])

  const { fromUser, ccContacts, bccContacts, files } = emailInfo

  const changeContacts = (value, property) => {
    dispatchState({
      type: ACT.CHANGE_EMAIL_CONTACTS,
      payload: {
        value,
        property
      }
    })
  }

  const changeFromContact = fromUser => {
    changeContacts(fromUser, 'fromUser')
    template &&
      getEmailTemplate(
        Array.isArray(contact) ? contact.map(c => c.id) : contact.id,
        template.value,
        fromUser.value,
        template => {
          setEmailBody(template)
        },
        app,
        leadId
      )
  }

  const users = useSelector(state => selectUsers(state.users))
  const companyContacts = useSelector(state => selectContacts(state.company))

  useEffect(() => {
    const contacts = companyContacts.concat(users)
    setAllCCContacts(contacts)
  }, [users, companyContacts])

  const dispatch = useDispatch()

  const setEmailTemplate = useCallback(
    selectedTemplate => {
      const { value } = selectedTemplate
      getEmailTemplate(
        Array.isArray(contact) ? contact.map(c => c.id) : contact.id,
        value,
        fromUser?.value,
        template => {
          setTemplate(selectedTemplate)
          setEmailBody(template.content)
          setSubject(template.subject)
        },
        app,
        leadId
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [contact, fromUser, app]
  )

  useEffect(() => {
    contact?.company?.id &&
      dispatch({
        type: ACT.COMPANY_CONTACTS_FETCH_REQUESTED,
        payload: {
          id: contact.company.id
        }
      })
  }, [dispatch, contact])

  useEffect(() => {
    dispatch({
      type: ACT.USERS_FETCH_REQUESTED
    })
  }, [dispatch])

  useEffect(() => {
    !emailTemplateType &&
      proposalId &&
      showEmailContactModal &&
      getProposalEmailBody(proposalId, setEmailBody)
  }, [emailTemplateType, proposalId, showEmailContactModal])

  useEffect(() => {
    !proposalId && showEmailContactModal && getEmailTemplates(app, setTemplates, !leadId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app, emailTemplateType, proposalId, showEmailContactModal])

  useEffect(() => {
    emailTemplateType &&
      showEmailContactModal &&
      templates.length &&
      getPreselectedEmailTemplate(emailTemplateType, templateId => {
        const opportunitiesTemplate = templates.find(t => t.value === templateId)
        setEmailTemplate(opportunitiesTemplate)
      })
  }, [emailTemplateType, showEmailContactModal, templates, setEmailTemplate])

  useEffect(() => {
    setToContact(contact)
  }, [contact])

  useEffect(() => {
    if (!showEmailContactModal) {
      dispatchState({
        type: ACT.RESET_EMAIL_CONTACTS,
        payload: {}
      })
      setTemplate(null)
      setSubject('')
      setEmailBody('')
    }
  }, [showEmailContactModal])

  if (isEmpty(fromUser)) {
    const { id, name } = getUser()
    changeContacts(
      {
        value: id,
        label: name
      },
      'fromUser'
    )
  }

  const sendEmail = (template, destroyEditor, splitMode) => {
    dispatch({
      type: ACT.EMAIL_CONTACT_REQUESTED,
      payload: {
        fromUser,
        toContact,
        ccContacts,
        bccContacts,
        subject,
        emailBody: template,
        files,
        fromContactsList,
        withSaveEmailAsNote,
        entityId,
        splitMode,
        app
      }
    })
    destroyEditor && destroyEditor()
    toggleEmailContactModal()
  }

  const uploadFiles = e => {
    const currentFiles = e.target.files

    for (const file of currentFiles) {
      const fr = new FileReader()
      fr.onload = () => {
        dispatchState({
          type: ACT.ADD_FILE,
          payload: {
            file
          }
        })
      }
      fr.readAsDataURL(file)
    }

    e.target.value = ''
  }

  const removeFile = file => {
    dispatchState({
      type: ACT.REMOVE_FILE,
      payload: {
        file
      }
    })
  }

  return (
    <EmailContactModal
      users={users}
      proposalId={proposalId}
      fromUser={fromUser}
      toContact={toContact}
      ccContacts={ccContacts}
      bccContacts={bccContacts}
      allCCContacts={allCCContacts}
      subject={subject}
      setSubject={setSubject}
      setToContact={setToContact}
      emailBody={emailBody}
      toggleEmailContactModal={toggleEmailContactModal}
      showEmailContactModal={showEmailContactModal}
      changeContacts={changeContacts}
      changeFromContact={changeFromContact}
      setEmailBody={setEmailBody}
      sendEmail={sendEmail}
      template={template}
      setEmailTemplate={setEmailTemplate}
      templates={templates}
      emailTemplateType={emailTemplateType}
      uploadFiles={uploadFiles}
      files={files}
      removeFile={removeFile}
      removeContact={removeContact}
      defaultEmailSplitMode={defaultEmailSplitMode}
      app={app}
    />
  )
}

export default EmailContactModalContainer
